import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getLocalDayEnd } from '@/lib/timezoneUtils';

export interface CallMetricsFilters {
  dateRange?: {
    from: Date;
    to: Date;
  };
  userIds?: string[];
  direction?: 'inbound' | 'outbound' | null;
}

export interface CallsByUser {
  userId: string;
  userName: string;
  totalCalls: number;
  inbound: number;
  outbound: number;
  completed: number;
  averageDuration: number;
  successfulCalls: number;
  answerRate: number;
  avgSuccessfulDuration: number;
}

export interface CallsByDate {
  date: string;
  total: number;
  inbound: number;
  outbound: number;
}

export interface CallDashboardMetrics {
  total_calls: number;
  inbound_calls: number;
  outbound_calls: number;
  completed_calls: number;
  scheduled_calls: number;
  missed_calls: number;
  total_duration: number;
  average_duration: number;
  deduplication_applied: boolean;
  calls_by_user: CallsByUser[];
  calls_by_date: CallsByDate[];
}

export function useCallMetricsRPC(filters: CallMetricsFilters = {}) {
  return useQuery({
    queryKey: [
      'call-metrics-rpc',
      filters.dateRange?.from?.toISOString(),
      filters.dateRange?.to?.toISOString(),
      filters.userIds,
      filters.direction,
    ],
    queryFn: async () => {
      console.log('📞 [CallMetrics RPC] Fetching with filters:', {
        dateRange: filters.dateRange ? {
          from: filters.dateRange.from?.toISOString(),
          to: filters.dateRange.to?.toISOString()
        } : 'none',
        userIds: filters.userIds,
        direction: filters.direction,
      });

      // Prepare date parameters
      const dateFrom = filters.dateRange?.from?.toISOString() || null;
      const dateTo = filters.dateRange?.to 
        ? getLocalDayEnd(filters.dateRange.to).toISOString() 
        : null;

      const { data, error } = await supabase.rpc('get_calls_dashboard_metrics', {
        p_date_from: dateFrom,
        p_date_to: dateTo,
        p_user_ids: filters.userIds && filters.userIds.length > 0 ? filters.userIds : null,
        p_direction: filters.direction || null,
      });

      if (error) {
        console.error('❌ [CallMetrics RPC] Error:', error);
        throw error;
      }

      const metrics = data as unknown as CallDashboardMetrics;

      console.log('✅ [CallMetrics RPC] Success:', {
        totalCalls: metrics?.total_calls,
        usersWithCalls: metrics?.calls_by_user?.length,
        deduplicationApplied: metrics?.deduplication_applied,
      });

      // Transform to match existing hook interface for backward compatibility
      return {
        totalCalls: metrics.total_calls,
        inboundCalls: metrics.inbound_calls,
        outboundCalls: metrics.outbound_calls,
        completedCalls: metrics.completed_calls,
        scheduledCalls: metrics.scheduled_calls,
        missedCalls: metrics.missed_calls,
        totalDuration: metrics.total_duration,
        averageDuration: metrics.average_duration,
        callsByUser: metrics.calls_by_user,
        callsByDate: metrics.calls_by_date.map(d => ({
          date: d.date,
          inbound: d.inbound,
          outbound: d.outbound,
          total: d.total,
        })),
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
  });
}
